import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import Bowser from 'bowser';
import { CREDIT_SCORE_GOODNESS } from '@/common/constants';
import { AppInitData } from '@/types/core';

export function isAndroid(): boolean {
	return Bowser.getParser(window.navigator.userAgent).getOSName() === 'Android';
}

export function isMobile(): boolean {
	return Bowser.getParser(window.navigator.userAgent).getPlatformType(true) === 'mobile';
}

export function isOverscrollNoneSupports(): boolean {
	return CSS && CSS.supports && CSS.supports('overscroll-behavior', 'none');
}

export function lockScroll(element: HTMLElement) {
	if (isAndroid() && isOverscrollNoneSupports()) { return; }
	disableBodyScroll(element, {
		reserveScrollBarGap: true,
	});
}

export function unlockScroll(element: HTMLElement) {
	if (isAndroid() && isOverscrollNoneSupports()) { return; }
	enableBodyScroll(element);
}

export function resetScrollLocks() {
	clearAllBodyScrollLocks();
}

export function camelToKebab(string: string): string {
	return string.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`);
}

export function extractJWT(jwt: string): object {
	try {
		return JSON.parse(atob(jwt.split('.')[1]));
	} catch (error) {
		throw new Error('Parse JWT error');
	}
}

export function hexToRgb(hex: string) {
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

	return result ? {
		r: parseInt(result[1], 16),
		g: parseInt(result[2], 16),
		b: parseInt(result[3], 16),
	} : null;
}

export function getCreditScoreGoodness(score: number): string {
	return Object.keys(CREDIT_SCORE_GOODNESS).find(
		key => score >= CREDIT_SCORE_GOODNESS[key].min && score <= CREDIT_SCORE_GOODNESS[key].max
	) || 'fair';
}

export function RGBAtoRGB(rgba: string, rbase: number, gbase: number, bbase: number) {
	const [r, g, b, a] = rgba.split('(')[1].split(')')[0].split(',').map(parseFloat);

	return {
		r: Math.round(((1 - a) * rbase) + (a * r)),
		g: Math.round(((1 - a) * gbase) + (a * g)),
		b: Math.round(((1 - a) * bbase) + (a * b)),
	};
}

export function isStorageWritable(storage: Storage = window.localStorage, key = 'ppStorage'): boolean {
	try {
		storage.setItem(`${key}Test`, 'Test');
		storage.removeItem(`${key}Test`);

		return true;
	} catch (error) {
		console.error(error); // eslint-disable-line no-console

		return false;
	}
}

export function isIntersectionObserverAvailable() {
	if ('IntersectionObserver' in window && 'IntersectionObserverEntry' in window
		&& 'intersectionRatio' in window.IntersectionObserverEntry.prototype) {
		// Minimal polyfill for Edge 15's lack of `isIntersecting`
		if (!('isIntersecting' in window.IntersectionObserverEntry.prototype)) {
			Object.defineProperty(window.IntersectionObserverEntry.prototype,
				'isIntersecting', {
					get() {
						return this.intersectionRatio > 0;
					},
				});
		}
		return true;
	}

	return false;
}

export function generateElement(elementData: string, elementId: string, elementType: string, elementLocation: string) {
	const elementExists = document.getElementById(elementId);

	if (elementExists !== null) {
		elementExists.remove();
	}
	const parentElement = document.getElementsByTagName(elementLocation)[0];
	const element = document.createElement(elementType);

	element.innerHTML = elementData;
	element.id = elementId;
	parentElement.appendChild(element);
}

export function generateMetaTag(name: string, content: string) {
	const metaTagExists = document.getElementById(`dr-${name}`);

	if (metaTagExists !== null) {
		metaTagExists.remove();
	}
	const head = document.getElementsByTagName('head')[0];
	const meta = document.createElement('meta');

	meta.name = name;
	meta.id = `dr-${name}`;
	meta.content = content;
	head.appendChild(meta);
}

export function generatePageUrl({ vdpUrl, vin, writeupId }: AppInitData, step = 'customize-deal') {
	return `${vdpUrl}#pp-next/${vin}/${step}/${writeupId}`;
}

export function setCookie(name: string, value: string, expiryDays: number) {
	const currentDate = new Date();

	currentDate.setTime(currentDate.getTime() + (expiryDays * 24 * 60 * 60 * 1000));
	const expires = `expires=${currentDate.toUTCString()}`;

	document.cookie = `${name}=${encodeURIComponent(value)};${expires};path=/`;
}

export function getCookie(cookieName: string) {
	const name = `${cookieName}=`;
	const decodedCookie = decodeURIComponent(document.cookie);
	let cookieList = decodedCookie.split(';');

	cookieList = cookieList.map(item => item.trim());
	const cookieData = cookieList.filter(item => item.indexOf(name) === 0);

	if (cookieData.length) {
		return cookieData[0].substring(name.length, cookieData[0].length);
	}
	return '';
}
